// Two processes, one file for the widget and the service (requirements Дп1–Дп5, Ап1–Ап5, Мг1–Мг3; models
// quint/specs/report_flow.qnt, report_stages.qnt, epic_process.qnt, stage_migrate.qnt). The processes are fixed: no settings.
//   downstream — the agent's reports: Бэклог → Требования → Валидация → Реализация → Деплой → Сделано (+ resolution);
//   upstream — epics: To Do → Discovery → Discovery Done → Delivery → Delivery Done (+ resolution).
// Their logic (stages, moves, resolutions) is separate; the board view (columns, resolution lanes) may be shared.

export const BOARD_STATUSES = [
  'backlog',
  'requirements',
  'validation',
  'implementation',
  'deploy',
  'fixed',
  'wont_fix',
  'duplicate',
  'closed',
] as const;
export type BoardStatus = (typeof BOARD_STATUSES)[number];
/**
 * Дп3, Пт7: names stored before 2026-11-01 (and the ones before 2026-10-31); rewritten once at startup (the previous
 * value is remembered, Мг1–Мг2), still accepted on write and in filters and read as the new stage.
 */
export const LEGACY_STATUS_ALIASES: Record<string, BoardStatus> = {
  selected: 'requirements',
  discovery: 'requirements',
  discovery_done: 'validation',
  delivery: 'implementation',
  new: 'backlog',
  in_progress: 'implementation',
  review: 'implementation',
  need_info: 'requirements',
};
export const LEGACY_STATUSES = Object.keys(LEGACY_STATUS_ALIASES);
/** Every value the status CHECK accepts. */
export const ACCEPTED_STATUSES: readonly string[] = [...BOARD_STATUSES, ...LEGACY_STATUSES];
/** Дп3: legacy name -> canonical one; anything else is returned as is. */
export const canonicalStatus = (status: string): string => LEGACY_STATUS_ALIASES[status] ?? status;
/** Дк1, Дп2: a resolution is the final status itself. */
export const CLOSING_STATUSES: readonly BoardStatus[] = ['fixed', 'wont_fix', 'duplicate', 'closed'];
/** Дк2: a report in a final status stays on the board this many days after its last change. */
export const BOARD_DONE_DAYS = 7;

/** Дп1: backlog, requirements, validation, implementation, deploy, done (final, with a resolution). */
export const STAGES = ['backlog', 'requirements', 'validation', 'implementation', 'deploy', 'done'] as const;
export type Stage = (typeof STAGES)[number];
export const STAGE_TITLES: Record<Stage, string> = {
  backlog: 'Бэклог',
  requirements: 'Требования',
  validation: 'Валидация',
  implementation: 'Реализация',
  deploy: 'Деплой',
  done: 'Сделано',
};
/** The status a move into a stage stores (Done: the default resolution). */
export const STAGE_STATUS: Record<Stage, BoardStatus> = {
  backlog: 'backlog',
  requirements: 'requirements',
  validation: 'validation',
  implementation: 'implementation',
  deploy: 'deploy',
  done: 'fixed',
};

export interface BoardColumn {
  id: string;
  title: string;
  statuses: string[];
}

export interface BoardConfig {
  columns: BoardColumn[];
}

/** Status words in messages; for a final status — the resolution. Legacy names read as their canonical ones. */
const STATUS_WORDS: Record<BoardStatus, string> = {
  backlog: 'бэклог',
  requirements: 'требования',
  validation: 'валидация',
  implementation: 'реализация',
  deploy: 'деплой',
  fixed: 'сделано',
  wont_fix: 'не будет сделано',
  duplicate: 'дубль',
  closed: 'закрыто',
};

export const STATUS_TITLES: Record<string, string> = {
  ...STATUS_WORDS,
  ...Object.fromEntries(Object.entries(LEGACY_STATUS_ALIASES).map(([legacy, to]) => [legacy, STATUS_WORDS[to]])),
};

const isFinal = (status: string): boolean => (CLOSING_STATUSES as readonly string[]).includes(status);

/** Пт2, Дп1: total on all accepted values; legacy names count as their canonical ones, finals are Done. */
export function stageOf(status: string): Stage {
  const value = canonicalStatus(status);
  if (isFinal(value)) return 'done';
  if (value === 'requirements' || value === 'validation' || value === 'implementation' || value === 'deploy') return value;
  return 'backlog';
}

/** Statuses stored under a stage, the default one first (the order of BOARD_STATUSES). */
export const stageStatuses = (stage: Stage): BoardStatus[] => BOARD_STATUSES.filter((s) => stageOf(s) === stage);

/** Пт3: the board starts at «Требования»; the backlog is a filter of the task list. */
export const REPORT_BOARD: BoardConfig = {
  columns: STAGES.filter((stage) => stage !== 'backlog').map((stage) => ({ id: stage, title: STAGE_TITLES[stage], statuses: stageStatuses(stage) })),
};

export interface BoardDomain {
  statuses: readonly string[];
  closing: readonly string[];
  /** Status words in messages. */
  titles: Record<string, string>;
}

export const REPORT_DOMAIN: BoardDomain = { statuses: BOARD_STATUSES, closing: CLOSING_STATUSES, titles: STATUS_TITLES };

/** Ап1: the epics process. */
export const EPIC_STATUSES = ['todo', 'discovery', 'discovery_done', 'delivery', 'delivery_done'] as const;
export type EpicStatus = (typeof EPIC_STATUSES)[number];
export const EPIC_STATUS_TITLES: Record<EpicStatus, string> = {
  todo: 'To Do',
  discovery: 'Discovery',
  discovery_done: 'Discovery Done',
  delivery: 'Delivery',
  delivery_done: 'Delivery Done',
};
/** Ап2: the epic's resolutions, set when it enters Delivery Done. */
export const EPIC_RESOLUTIONS = ['fixed', 'wont_fix', 'closed'] as const;
export type EpicResolution = (typeof EPIC_RESOLUTIONS)[number];
export const EPIC_RESOLUTION_TITLES: Record<EpicResolution, string> = { fixed: 'сделано', wont_fix: 'не будет сделано', closed: 'закрыто' };
/** Ап4: names stored before 2026-11-01: the stage, and the resolution for the ones that ended the epic. */
export const EPIC_LEGACY_ALIASES: Record<string, { status: EpicStatus; resolution: EpicResolution | null }> = {
  in_progress: { status: 'delivery', resolution: null },
  done: { status: 'delivery_done', resolution: 'fixed' },
  cancelled: { status: 'delivery_done', resolution: 'wont_fix' },
};
export const ACCEPTED_EPIC_STATUSES: readonly string[] = [...EPIC_STATUSES, ...Object.keys(EPIC_LEGACY_ALIASES)];
export const EPIC_CLOSING_STATUS: EpicStatus = 'delivery_done';
/** Ап4: a legacy epic status becomes its stage (+ resolution); a current one is returned as is, without a resolution. */
export function canonicalEpicStatus(status: string): { status: string; resolution: string | null } {
  return EPIC_LEGACY_ALIASES[status] ?? { status, resolution: null };
}
export const EPIC_DOMAIN: BoardDomain = {
  statuses: EPIC_STATUSES,
  closing: [EPIC_CLOSING_STATUS],
  titles: { ...EPIC_STATUS_TITLES, ...Object.fromEntries(Object.entries(EPIC_LEGACY_ALIASES).map(([legacy, to]) => [legacy, EPIC_STATUS_TITLES[to.status]])) },
};
/** Ап5: the epics board — one column per stage, the resolution lanes on the last one. */
export const EPIC_BOARD: BoardConfig = {
  columns: EPIC_STATUSES.map((status) => ({ id: status, title: EPIC_STATUS_TITLES[status], statuses: [status] })),
};

export function columnOf(config: BoardConfig, status: string): BoardColumn | null {
  return config.columns.find((column) => column.statuses.includes(canonicalStatus(status))) ?? null;
}

/** Дк1: the resolution is shown only on a final status; null otherwise. */
export function resolutionOf(status: string): string | null {
  return isFinal(status) ? STATUS_TITLES[status] : null;
}

/**
 * Дп1: forward one stage at a time, but into Implementation from any earlier stage; into Done from any stage; back to any
 * lower stage; out of Done only to Backlog or Implementation. Inside one stage a different status is a plain relabel
 * (aliases, resolution).
 */
export function allowedMove(from: string, to: string): boolean {
  if (from === to) return false;
  const a = STAGES.indexOf(stageOf(from));
  const b = STAGES.indexOf(stageOf(to));
  if (a === b) return true;
  const done = STAGES.indexOf('done');
  const implementation = STAGES.indexOf('implementation');
  if (b === done) return true;
  if (a === done) return b === 0 || b === implementation;
  if (b === implementation) return true;
  return b < a || b - a === 1;
}

/** The reason a move is refused, in Russian; null when it is allowed. */
export function moveProblem(from: string, to: string): string | null {
  if (allowedMove(from, to)) return null;
  const a = STAGE_TITLES[stageOf(from)];
  const b = STAGE_TITLES[stageOf(to)];
  if (from === to) return `Репорт уже в статусе «${STATUS_TITLES[from] ?? from}».`;
  if (stageOf(from) === 'done') return `Из «Сделано» вернуть можно только в «Бэклог» или «Реализацию»: «${a}» → «${b}» нельзя.`;
  return `Этапы идут по одному (в «Реализацию» и «Сделано» — из любого): «${a}» → «${b}» нельзя.`;
}

/** Ап1: epics move freely between stages (a person drags the card); only the resolution rule (Ап2) and open reports (Ап3) hold. */
export const epicMoveAllowed = (from: string, to: string): boolean => canonicalEpicStatus(from).status !== canonicalEpicStatus(to).status;

/** Ап2: the problem of entering Delivery Done without a valid resolution, or of a resolution outside it; null when fine. */
export function epicResolutionProblem(status: string, resolution: string | null | undefined): string | null {
  const value = resolution ?? null;
  if (status === EPIC_CLOSING_STATUS) {
    if (value === null) return 'В Delivery Done эпик переводится с резолюцией: сделано, не будет сделано или закрыто.';
    if (!(EPIC_RESOLUTIONS as readonly string[]).includes(value)) return `Резолюция эпика — ${EPIC_RESOLUTIONS.join(', ')}.`;
    return null;
  }
  return value === null ? null : 'Резолюция эпика есть только в Delivery Done.';
}
